import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { toast } from 'react-toastify';
import { faBullhorn, faCalendarCheck, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { BroadcastAudience, BroadcastStatus } from '../../../__generated__/graphql';
import {
  useAdminBroadcastsQuery,
  useAdminCreateBroadcastMutation,
  useDeleteBroadcastMutation,
  useAdminScheduleBroadcastMutation,
  useAdminSendBroadcastMutation,
  useAdminUnscheduleBroadcastMutation,
  useAdminUpdateBroadcastMutation,
  useBroadcastAudienceSizeLazyQuery,
  useBroadcastAudienceSizeQuery,
} from '../../services/notificationService/__generated__/queries.generated';
import { getBrand } from '../../constants/brands';
import { Button } from '../../components/button';
import { Modal } from '../../components/modal';
import {
  Alert,
  Badge,
  Card,
  CardBody,
  CardRow,
  EmptyState,
  Field,
  Input,
  LoadingBlock,
  PageContainer,
  PageHeader,
  Select,
  Stack,
  Tabs,
  Textarea,
} from '../../components/ui';
import {
  AdminBroadcast,
  AUDIENCE_OPTIONS,
  BroadcastDraft,
  DraftErrors,
  SUMMARY_SOFT_MAX,
  TITLE_MAX,
  audienceLabel,
  broadcastActions,
  broadcastStatusView,
  defaultScheduleValue,
  fromDateTimeLocalValue,
  toDateTimeLocalValue,
  validateDraft,
  validateScheduleTime,
} from './broadcastAdmin';

const PAGE_SIZE = 20;
/** Every list query, refetched after any change so the tab counts stay right. */
const REFETCH = ['AdminBroadcasts'];

type TabKey = 'drafts' | 'scheduled' | 'sent';
type When = 'draft' | 'now' | 'schedule';

const Body = styled.div`
  ${tw`min-w-0 flex-1`}
`;
const TitleLine = styled.div`
  ${tw`flex flex-wrap items-center gap-2 text-sm font-semibold`}
  color: var(--color-text-primary);
`;
const SummaryLine = styled.p`
  ${tw`text-sm mt-1`}
  color: var(--color-text-secondary);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;
const MetaLine = styled.div`
  ${tw`text-xs mt-1`}
  color: var(--color-text-tertiary);
`;
const RowAlert = styled.div`
  ${tw`mt-2`}
`;
const Actions = styled.div`
  ${tw`flex flex-wrap items-center justify-end gap-2 shrink-0`}
`;
const LoadMore = styled.div`
  ${tw`flex justify-center p-4`}
`;
const Composer = styled.form`
  ${tw`flex flex-col gap-4`}
`;
const ComposerTitle = styled.h2`
  ${tw`text-lg font-semibold`}
  color: var(--color-text-primary);
`;
const Counter = styled.span<{ $over?: boolean }>`
  ${tw`text-xs`}
  color: ${({ $over }) => ($over ? 'var(--color-warning, #b45309)' : 'var(--color-text-tertiary)')};
`;
const WhenGroup = styled.div`
  ${tw`flex flex-col gap-2`}
`;
const WhenOption = styled.label<{ $active: boolean }>`
  ${tw`flex items-start gap-3 rounded-md p-3 cursor-pointer text-sm`}
  border: 1px solid
    ${({ $active }) => ($active ? 'var(--color-black)' : 'var(--color-border)')};
  color: var(--color-text-primary);
  input {
    margin-top: 3px;
  }
`;
const WhenHint = styled.span`
  ${tw`block text-xs mt-0.5`}
  color: var(--color-text-secondary);
`;
const Preview = styled.div`
  ${tw`rounded-md p-3`}
  background-color: var(--color-bg-secondary);
`;
const PreviewLabel = styled.div`
  ${tw`text-xs font-medium uppercase tracking-wide mb-1`}
  color: var(--color-text-tertiary);
`;
const ComposerFooter = styled.div`
  ${tw`flex flex-wrap justify-end gap-2 pt-2`}
`;

const EMPTY_DRAFT: BroadcastDraft = {
  title: '',
  summary: '',
  content: '',
  audience: BroadcastAudience.AllUsers,
};

interface ComposerState {
  editing: AdminBroadcast | null;
  draft: BroadcastDraft;
  when: When;
  scheduleAt: string;
}

function openComposer(editing: AdminBroadcast | null): ComposerState {
  if (!editing) {
    return { editing: null, draft: EMPTY_DRAFT, when: 'draft', scheduleAt: defaultScheduleValue() };
  }
  const scheduled = editing.status === BroadcastStatus.Scheduled && editing.scheduledAt;
  return {
    editing,
    draft: {
      title: editing.title,
      summary: editing.summary,
      content: editing.content,
      audience: editing.audience,
    },
    when: scheduled ? 'schedule' : 'draft',
    scheduleAt: scheduled
      ? toDateTimeLocalValue(new Date(editing.scheduledAt as string))
      : defaultScheduleValue(),
  };
}

function errorMessage(err: unknown, fallback: string): string {
  return (err as { message?: string })?.message || fallback;
}

/**
 * Broadcasts to this brand's users (spec 011 R2). The API keeps every list,
 * count and send on the brand of this site, so this page needs no brand logic.
 */
export function AdminBroadcastsPage() {
  const brand = getBrand();
  const [tab, setTab] = useState<TabKey>('drafts');
  const [composer, setComposer] = useState<ComposerState | null>(null);
  const [errors, setErrors] = useState<DraftErrors>({});
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const listOptions = (status: BroadcastStatus) => ({
    variables: { status, page: 1, limit: PAGE_SIZE },
    fetchPolicy: 'cache-and-network' as const,
    notifyOnNetworkStatusChange: true,
  });
  const drafts = useAdminBroadcastsQuery(listOptions(BroadcastStatus.Draft));
  const scheduled = useAdminBroadcastsQuery(listOptions(BroadcastStatus.Scheduled));
  const sent = useAdminBroadcastsQuery(listOptions(BroadcastStatus.Sent));
  const lists = { drafts, scheduled, sent };
  const active = lists[tab];

  const mutationOptions = { refetchQueries: REFETCH, awaitRefetchQueries: true };
  const [createBroadcast] = useAdminCreateBroadcastMutation(mutationOptions);
  const [updateBroadcast] = useAdminUpdateBroadcastMutation(mutationOptions);
  const [sendBroadcast] = useAdminSendBroadcastMutation(mutationOptions);
  const [scheduleBroadcast] = useAdminScheduleBroadcastMutation(mutationOptions);
  const [unscheduleBroadcast] = useAdminUnscheduleBroadcastMutation(mutationOptions);
  const [deleteBroadcast] = useDeleteBroadcastMutation(mutationOptions);

  const audience = composer?.draft.audience ?? BroadcastAudience.AllUsers;
  const audienceSize = useBroadcastAudienceSizeQuery({
    variables: { audience },
    skip: !composer,
    fetchPolicy: 'network-only',
  });
  const reach = audienceSize.data?.broadcastAudienceSize;
  // For the row-level "Send now": the count of that row's audience, fetched on demand.
  const [countAudience] = useBroadcastAudienceSizeLazyQuery({ fetchPolicy: 'network-only' });

  const items = (active.data?.allBroadcasts.items ?? []) as AdminBroadcast[];
  const tabItems = useMemo(
    () =>
      (['drafts', 'scheduled', 'sent'] as TabKey[]).map(key => ({
        key,
        label: key === 'drafts' ? 'Drafts' : key === 'scheduled' ? 'Scheduled' : 'Sent',
        count: lists[key].data?.allBroadcasts.totalCount,
        'data-testid': `broadcast-tab-${key}`,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [drafts.data, scheduled.data, sent.data],
  );

  const updateComposer = (patch: Partial<ComposerState>) =>
    setComposer(current => (current ? { ...current, ...patch } : current));
  const updateDraft = (patch: Partial<BroadcastDraft>) =>
    setComposer(current =>
      current ? { ...current, draft: { ...current.draft, ...patch } } : current,
    );

  const confirmSend = (title: string, count: number | undefined) =>
    window.confirm(
      `Send "${title}" now${
        count === undefined
          ? ''
          : ` to ${count.toLocaleString()} ${count === 1 ? 'person' : 'people'}`
      } on ${brand.name}? It goes to their notifications and as a push. This cannot be undone.`,
    );

  const submitComposer = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!composer) return;
    const nextErrors = validateDraft(composer.draft);
    if (composer.when === 'schedule') {
      const timeError = validateScheduleTime(composer.scheduleAt);
      if (timeError) nextErrors.scheduledAt = timeError;
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    if (composer.when === 'now' && !confirmSend(composer.draft.title, reach)) return;

    setSaving(true);
    // Set only once the text is stored, so the error below never claims a save that failed.
    let saved: AdminBroadcast | undefined;
    try {
      const input = {
        title: composer.draft.title.trim(),
        summary: composer.draft.summary.trim(),
        content: composer.draft.content.trim(),
        audience: composer.draft.audience,
      };
      if (composer.editing) {
        const updated = await updateBroadcast({ variables: { id: composer.editing.id, input } });
        saved = updated.data?.updateBroadcast as AdminBroadcast | undefined;
      } else {
        const created = await createBroadcast({ variables: { input } });
        saved = created.data?.createBroadcast as AdminBroadcast | undefined;
      }
      if (!saved) throw new Error('The broadcast was not saved');
      const savedId = saved.id;

      if (composer.when === 'now') {
        await sendBroadcast({ variables: { broadcastId: savedId } });
        toast.success('Broadcast sent');
        setTab('sent');
      } else if (composer.when === 'schedule') {
        const at = fromDateTimeLocalValue(composer.scheduleAt) as Date;
        await scheduleBroadcast({
          variables: { broadcastId: savedId, scheduledAt: at.toISOString() },
        });
        toast.success(`Scheduled for ${at.toLocaleString()}`);
        setTab('scheduled');
      } else if (composer.editing?.status === BroadcastStatus.Scheduled) {
        // Saving a scheduled broadcast as a draft takes it off the schedule.
        await unscheduleBroadcast({ variables: { broadcastId: savedId } });
        toast.success('Saved as a draft');
        setTab('drafts');
      } else {
        toast.success('Draft saved');
        setTab('drafts');
      }
      setComposer(null);
    } catch (err) {
      // Saved but not sent or scheduled: keep it, say so, and let the admin retry from the list.
      toast.error(
        saved && composer.when !== 'draft'
          ? `Saved as a draft, but ${composer.when === 'now' ? 'sending' : 'scheduling'} failed: ${errorMessage(err, 'unknown error')}`
          : errorMessage(err, 'Could not save the broadcast'),
      );
      if (saved) {
        // The composer now edits the stored broadcast, so a retry updates instead of creating again.
        const stored = saved;
        setComposer(current => (current ? { ...current, editing: stored } : current));
      }
    } finally {
      setSaving(false);
    }
  };

  const runRowAction = async (
    broadcast: AdminBroadcast,
    action: 'send' | 'unschedule' | 'remove',
  ) => {
    if (action === 'send') {
      // Same confirmation as the composer: say how many people it reaches.
      const { data } = await countAudience({ variables: { audience: broadcast.audience } });
      if (!confirmSend(broadcast.title, data?.broadcastAudienceSize)) return;
    } else if (
      action === 'remove' &&
      !window.confirm(`Delete "${broadcast.title}"? This cannot be undone.`)
    ) {
      return;
    }
    setBusyId(broadcast.id);
    try {
      if (action === 'send') {
        const result = await sendBroadcast({ variables: { broadcastId: broadcast.id } });
        const count = result.data?.sendBroadcast.recipientCount ?? 0;
        toast.success(`Sent to ${count.toLocaleString()} ${count === 1 ? 'person' : 'people'}`);
      } else if (action === 'unschedule') {
        await unscheduleBroadcast({ variables: { broadcastId: broadcast.id } });
        toast.success('Moved back to drafts');
      } else {
        await deleteBroadcast({ variables: { id: broadcast.id } });
        toast.success('Broadcast deleted');
      }
    } catch (err) {
      toast.error(errorMessage(err, 'Something went wrong'));
    } finally {
      setBusyId(null);
    }
  };

  const loadMore = () =>
    active.fetchMore({
      variables: { page: (active.data?.allBroadcasts.page ?? 1) + 1 },
      updateQuery: (prev, { fetchMoreResult }) =>
        fetchMoreResult
          ? {
              allBroadcasts: {
                ...fetchMoreResult.allBroadcasts,
                items: [...prev.allBroadcasts.items, ...fetchMoreResult.allBroadcasts.items],
              },
            }
          : prev,
    });

  const emptyCopy: Record<TabKey, { icon: typeof faBullhorn; title: string; description: string }> =
    {
      drafts: {
        icon: faBullhorn,
        title: 'No drafts',
        description: `Write an announcement for your users on ${brand.name}. It lands in their notifications and as a push.`,
      },
      scheduled: {
        icon: faCalendarCheck,
        title: 'Nothing scheduled',
        description: 'Scheduled broadcasts go out within a minute of their time.',
      },
      sent: {
        icon: faPaperPlane,
        title: 'Nothing sent yet',
        description: 'Sent broadcasts and how many people they reached show here.',
      },
    };

  const summaryLength = composer?.draft.summary.length ?? 0;
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <PageContainer width="lg" canvas>
      <PageHeader
        eyebrow="Admin"
        title="Broadcasts"
        description={`Announcements to your users on ${brand.name}. Each one appears in their notification center and as a push notification. Other brands never see it.`}
        actions={
          <Button
            text="New broadcast"
            size="sm"
            type="button"
            dataTestId="new-broadcast"
            onClick={() => {
              setErrors({});
              setComposer(openComposer(null));
            }}
          />
        }
      />

      <Stack>
        <Tabs items={tabItems} value={tab} onChange={setTab} />

        <Card>
          {active.loading && !active.data && <LoadingBlock label="Loading broadcasts" />}
          {active.error && (
            <CardBody>
              <Alert tone="error" title="Could not load broadcasts">
                {active.error.message}
              </Alert>
            </CardBody>
          )}
          {!active.error && active.data && items.length === 0 && (
            <EmptyState
              icon={emptyCopy[tab].icon}
              title={emptyCopy[tab].title}
              description={emptyCopy[tab].description}
            />
          )}
          {items.map(broadcast => {
            const view = broadcastStatusView(broadcast);
            const can = broadcastActions(broadcast);
            const busy = busyId === broadcast.id;
            return (
              <CardRow key={broadcast.id} data-testid="broadcast-row">
                <Body>
                  <TitleLine>
                    {broadcast.title}
                    <Badge tone={view.tone} dot size="sm">
                      {view.label}
                    </Badge>
                    <Badge tone="neutral" size="sm">
                      {audienceLabel(broadcast.audience)}
                    </Badge>
                  </TitleLine>
                  <SummaryLine>{broadcast.summary}</SummaryLine>
                  <MetaLine>{view.detail}</MetaLine>
                  {view.problem && (
                    <RowAlert>
                      <Alert tone={view.tone === 'error' ? 'error' : 'warning'}>
                        {view.problem}
                      </Alert>
                    </RowAlert>
                  )}
                </Body>
                <Actions>
                  {can.edit && (
                    <Button
                      text="Edit"
                      theme="secondary"
                      size="sm"
                      type="button"
                      isDisabled={busy}
                      onClick={() => {
                        setErrors({});
                        setComposer(openComposer(broadcast));
                      }}
                    />
                  )}
                  {can.unschedule && (
                    <Button
                      text="Unschedule"
                      theme="secondary"
                      size="sm"
                      type="button"
                      isDisabled={busy}
                      onClick={() => runRowAction(broadcast, 'unschedule')}
                    />
                  )}
                  {can.send && (
                    <Button
                      text={broadcast.sendAttempts > 0 ? 'Send again' : 'Send now'}
                      size="sm"
                      type="button"
                      isLoading={busy}
                      onClick={() => runRowAction(broadcast, 'send')}
                    />
                  )}
                  {can.remove && (
                    <Button
                      text="Delete"
                      theme="text"
                      size="sm"
                      type="button"
                      isDisabled={busy}
                      onClick={() => runRowAction(broadcast, 'remove')}
                    />
                  )}
                </Actions>
              </CardRow>
            );
          })}
          {active.data?.allBroadcasts.hasMore && (
            <LoadMore>
              <Button
                text="Load more"
                theme="secondary"
                size="sm"
                type="button"
                isLoading={active.loading}
                onClick={loadMore}
              />
            </LoadMore>
          )}
        </Card>
      </Stack>

      <Modal
        isOpen={Boolean(composer)}
        onClose={() => !saving && setComposer(null)}
        title={composer?.editing ? 'Edit broadcast' : 'New broadcast'}
        modalWidth="640px"
      >
        {composer && (
          <Composer onSubmit={submitComposer} noValidate data-testid="broadcast-composer">
            <ComposerTitle>{composer.editing ? 'Edit broadcast' : 'New broadcast'}</ComposerTitle>

            <Field
              label="Title"
              htmlFor="broadcast-title"
              error={errors.title}
              hint={<Counter>{`${composer.draft.title.length}/${TITLE_MAX}`}</Counter>}
            >
              <Input
                id="broadcast-title"
                value={composer.draft.title}
                maxLength={TITLE_MAX}
                onChange={e => updateDraft({ title: e.target.value })}
                placeholder="New airport pickup at SFO"
              />
            </Field>

            <Field
              label="Summary"
              htmlFor="broadcast-summary"
              error={errors.summary}
              hint={
                <Counter $over={summaryLength > SUMMARY_SOFT_MAX}>
                  {summaryLength > SUMMARY_SOFT_MAX
                    ? `${summaryLength} characters: phones cut push text after about ${SUMMARY_SOFT_MAX}`
                    : `The push notification text. ${summaryLength}/${SUMMARY_SOFT_MAX}`}
                </Counter>
              }
            >
              <Textarea
                id="broadcast-summary"
                rows={2}
                value={composer.draft.summary}
                onChange={e => updateDraft({ summary: e.target.value })}
              />
            </Field>

            <Field
              label="Message"
              htmlFor="broadcast-content"
              error={errors.content}
              hint="Plain text, shown when someone opens the notification. Line breaks are kept."
            >
              <Textarea
                id="broadcast-content"
                rows={7}
                value={composer.draft.content}
                onChange={e => updateDraft({ content: e.target.value })}
              />
            </Field>

            <Field
              label="Audience"
              htmlFor="broadcast-audience"
              hint={
                audienceSize.loading
                  ? 'Counting…'
                  : reach === undefined
                    ? AUDIENCE_OPTIONS.find(o => o.value === audience)?.description
                    : `${AUDIENCE_OPTIONS.find(o => o.value === audience)?.description}. Reaches ${reach.toLocaleString()} ${reach === 1 ? 'person' : 'people'} right now.`
              }
            >
              <Select
                id="broadcast-audience"
                value={audience}
                onChange={e => updateDraft({ audience: e.target.value as BroadcastAudience })}
              >
                {AUDIENCE_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </Field>

            <WhenGroup role="radiogroup" aria-label="When to send">
              {(
                [
                  ['draft', 'Save as draft', 'Nothing is sent. Send or schedule it later.'],
                  ['now', 'Send now', 'Goes out as soon as you confirm.'],
                  ['schedule', 'Schedule', `Goes out within a minute of the time (${timeZone}).`],
                ] as [When, string, string][]
              ).map(([value, label, hint]) => (
                <WhenOption key={value} $active={composer.when === value}>
                  <input
                    type="radio"
                    name="broadcast-when"
                    value={value}
                    checked={composer.when === value}
                    onChange={() => updateComposer({ when: value })}
                  />
                  <span>
                    {label}
                    <WhenHint>{hint}</WhenHint>
                  </span>
                </WhenOption>
              ))}
            </WhenGroup>

            {composer.when === 'schedule' && (
              <Field label="Send at" htmlFor="broadcast-schedule" error={errors.scheduledAt}>
                <Input
                  id="broadcast-schedule"
                  type="datetime-local"
                  value={composer.scheduleAt}
                  min={toDateTimeLocalValue(new Date())}
                  onChange={e => updateComposer({ scheduleAt: e.target.value })}
                />
              </Field>
            )}

            {(composer.draft.title || composer.draft.summary) && (
              <Preview aria-label="Notification preview">
                <PreviewLabel>Notification preview</PreviewLabel>
                <TitleLine>{composer.draft.title || 'Title'}</TitleLine>
                <SummaryLine>{composer.draft.summary || 'Summary'}</SummaryLine>
              </Preview>
            )}

            <ComposerFooter>
              <Button
                text="Cancel"
                theme="secondary"
                type="button"
                isDisabled={saving}
                onClick={() => setComposer(null)}
              />
              <Button
                text={
                  composer.when === 'now'
                    ? 'Send now'
                    : composer.when === 'schedule'
                      ? 'Schedule'
                      : 'Save draft'
                }
                type="submit"
                dataTestId="broadcast-submit"
                isLoading={saving}
              />
            </ComposerFooter>
          </Composer>
        )}
      </Modal>
    </PageContainer>
  );
}

export default AdminBroadcastsPage;
